// [AUTH_GOOGLE_V1] Utility Helpers for PWA and Auth Stability

export const isIOS = () => {
    if (typeof window === 'undefined') return false;
    const isStandardIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // Check for iPadOS (MacIntel + touchpoints)
    const isIPadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isStandardIOS || isIPadOS;
};

export const isPWA = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://');
};

// --- storageSafe Logic ---

export const safeSet = (key, val) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, val);
        } else if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(key, val);
        }
    } catch (e) {
        console.warn("[AUTH_GOOGLE] safeSet failed", e);
    }
};

export const safeGet = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(key);
        } else if (typeof sessionStorage !== 'undefined') {
            return sessionStorage.getItem(key);
        }
    } catch (e) {
        return null;
    }
    return null;
};

export const safeRemove = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(key);
        }
    } catch (e) {
        // no-op
    }
};

// --- TTL Logic ---

export const setLocalWithTTL = (key, value, ttlMs) => {
    const item = {
        value: value,
        expiry: Date.now() + ttlMs,
    };
    safeSet(key, JSON.stringify(item));
};

export const getLocalWithTTL = (key) => {
    const itemStr = safeGet(key);
    if (!itemStr) return null;
    try {
        const item = JSON.parse(itemStr);
        if (Date.now() > item.expiry) {
            safeRemove(key);
            return null;
        }
        return item.value;
    } catch (e) {
        safeRemove(key);
        return null;
    }
};

// --- Auth Logs Ring Buffer ---

export const logAuth = (msg, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = { ts: timestamp, msg: msg, data: data };

    // Add to window buffer (volatile)
    if (typeof window !== 'undefined') {
        if (!window._AUTH_LOGS_) window._AUTH_LOGS_ = [];
        window._AUTH_LOGS_.push(logEntry);
        if (window._AUTH_LOGS_.length > 50) window._AUTH_LOGS_.shift();
    }

    // Persist to localStorage with TTL 30min
    try {
        const currentLogsStr = safeGet("authDebugLogs");
        let currentLogs = [];
        if (currentLogsStr) {
            const parsed = JSON.parse(currentLogsStr);
            // Check expiry manually or via helper
            if (Date.now() < parsed.expiry) {
                currentLogs = parsed.value;
            }
        }
        currentLogs.push(logEntry);
        if (currentLogs.length > 50) currentLogs.shift();

        setLocalWithTTL("authDebugLogs", currentLogs, 30 * 60 * 1000); // 30 min
    } catch (e) {
        // Silent fail
    }

    //console.log(`[AUTH_GOOGLE] ${msg}`, data || "");
};

/**
 * [V2.0.0] Shared Validation Utilities
 */

export const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
};

/**
 * Normalizes phone numbers to E.164 format
 * @param {string} input - Raw phone input
 * @param {string} countryPhoneCode - Default country code (e.g. "33")
 */
export const normalizePhoneToE164 = (input, countryPhoneCode) => {
    if (!input) return null;
    let clean = String(input).replace(/[\s.-]/g, "");
    
    if (clean.startsWith("00")) clean = "+" + clean.substring(2);

    // Special handling for FR (33)
    if (countryPhoneCode === "33" && clean.startsWith("0") && clean.length === 10) {
        clean = "+33" + clean.substring(1);
    } else if (!clean.startsWith("+")) {
        clean = "+" + countryPhoneCode + clean;
    }

    // Strict E.164 Regex validation
    const e164Regex = /^\+[1-9]\d{7,14}$/;
    return e164Regex.test(clean) ? clean : null;
};

/**
 * Positive whitelist check: determine if a booking represents a driver ride actively in progress.
 */
export const isDriverRideInProgress = (booking, nowMs = Date.now()) => {
    if (!booking || typeof booking !== 'object') return false;
    const status = String(booking.status || '').toUpperCase().trim();
    const tripStatus = String(booking.trip_status || '').toUpperCase().trim();

    // 0. Completed, cancelled, paid or terminated bookings are NEVER in progress
    const terminalStatuses = ['COMPLETE', 'COMPLETED', 'PAID', 'CANCELLED', 'REJECTED', 'EXPIRED', 'PENDING_PAYMENT_FAILED'];
    if (terminalStatuses.includes(status) || terminalStatuses.includes(tripStatus)) {
        return false;
    }

    // 1. Course engagée / en cours d'exécution
    if (['STARTED', 'ARRIVED'].includes(status)) return true;
    if (['DRIVER_ON_THE_WAY', 'ARRIVED', 'STARTED', 'REACHED'].includes(tripStatus)) return true;

    // 2. Réservation immédiate acceptée
    if (status === 'ACCEPTED') {
        const isImmediate = booking.bookLater === false || (
            booking.bookLater !== true &&
            (!booking.bookingDate || isNaN(Date.parse(booking.bookingDate)))
        );
        if (isImmediate) return true;
    }
    return false;
};

/**
 * Find active rides for a driver and detect conflicts.
 */
export const findDriverActiveRide = (bookings, driverUid, nowMs = Date.now()) => {
    if (!bookings || !driverUid) return { activeRides: [], activeRide: null, hasConflict: false };
    const list = Array.isArray(bookings) ? bookings : Object.values(bookings);
    const activeRides = [];
    for (const b of list) {
        if (!b || typeof b !== 'object') continue;
        const isAssigned = (
            b.driver === driverUid ||
            b.driverId === driverUid ||
            b.driverUid === driverUid ||
            b.driver_uid === driverUid ||
            b.driver?.uid === driverUid
        );
        if (isAssigned && isDriverRideInProgress(b, nowMs)) {
            activeRides.push(b);
        }
    }
    return {
        activeRides,
        activeRide: activeRides.length === 1 ? activeRides[0] : null,
        hasConflict: activeRides.length > 1
    };
};

/**
 * Determine the exact operational service status of a vehicle for admin consultation:
 * - 'Hors ligne'
 * - 'En ligne (libre)'
 * - 'En ligne (en course)'
 */
export const getVehicleServiceStatus = (car, driverUser, bookings = [], availableDrivers = null) => {
    if (!car || !car.id) return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };

    // 0. Car must be approved to be in service
    if (!car.approved && !car.carApproved) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 1. Check if driver exists and is approved / active
    if (!driverUser || typeof driverUser !== 'object') {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    const driverUid = driverUser.uid || driverUser.id || car.driverId || car.driver;

    // 1a. If the live available drivers pool is provided (e.g. from fetchDrivers / usersdata.drivers),
    // cross-reference it: a driver MUST be in the active drivers pool to have a vehicle in service.
    if (Array.isArray(availableDrivers)) {
        const isDriverInPool = availableDrivers.some(d => d && (
            (d.id && String(d.id) === String(driverUid)) ||
            (d.uid && String(d.uid) === String(driverUid))
        ));
        if (!isDriverInPool) {
            return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
        }
    }

    // 1b. A driver MUST have a valid and fresh GPS location to be considered connected and in service.
    // If location is missing, has invalid coordinates, or is excessively stale (> 10 mins), the driver is considered offline.
    const hasValidLocation = Boolean(
        driverUser.location &&
        driverUser.location.lat != null &&
        driverUser.location.lng != null &&
        !isNaN(Number(driverUser.location.lat)) &&
        !isNaN(Number(driverUser.location.lng))
    );
    const locTimestamp = (driverUser.location?.timestamp != null)
        ? Number(driverUser.location.timestamp)
        : (driverUser.location?.updatedAt != null ? Number(driverUser.location.updatedAt) : null);
    const isLocStale = locTimestamp != null && !isNaN(locTimestamp) && (Date.now() - locTimestamp > 10 * 60 * 1000);

    if ((!hasValidLocation || isLocStale) && !Array.isArray(availableDrivers)) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    const isDriverOnline = Boolean(
        driverUser.driverActiveStatus === true &&
        driverUser.approved === true &&
        !driverUser.accountDeleted &&
        !driverUser.isDeleted &&
        driverUser.isActive !== false &&
        driverUser.activeMode !== 'customer'
    );
    if (!isDriverOnline) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 2. Check if this specific car is the driver's active on-duty vehicle
    let isVehicleInService = false;
    if (driverUser.activeCarId) {
        isVehicleInService = String(driverUser.activeCarId) === String(car.id);
    } else if (driverUser.carType) {
        isVehicleInService = String(driverUser.carType).trim().toLowerCase() === String(car.carType || '').trim().toLowerCase();
    } else {
        isVehicleInService = car.active === true;
    }
    if (!isVehicleInService) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 3. Check if driver is currently in a ride
    const isQueue = Boolean(driverUser.queue);
    const hasRideInProgress = (Array.isArray(bookings) ? bookings : Object.values(bookings || {})).some(b => {
        if (!b || typeof b !== 'object') return false;
        const isAssigned = (
            b.driver === driverUid ||
            b.driverId === driverUid ||
            b.driverUid === driverUid ||
            b.driver_uid === driverUid ||
            b.driver?.uid === driverUid
        );
        if (!isAssigned) return false;
        return isDriverRideInProgress(b);
    });

    if (isQueue || hasRideInProgress) {
        return { status: 'ON_DUTY_BUSY', label: 'En ligne (en course)', color: 'busy' };
    }

    return { status: 'ON_DUTY_FREE', label: 'En ligne (libre)', color: 'free' };
};

/**
 * Sélectionne au maximum maxPerCategory chauffeurs les plus proches par catégorie active
 * pour optimiser les quotas et coûts Google Routes API sans dégrader la précision.
 */
export const filterTopCandidatesByCategory = (drivers = [], cars = [], maxPerCategory = 2) => {
    if (!Array.isArray(drivers) || drivers.length === 0) return [];

    const norm = (s) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/_/g, ' ').replace(/\s+/g, ' ');
    const byCategory = new Map();

    drivers.forEach(driver => {
        if (!driver || !driver.location || driver.location.lat == null || driver.location.lng == null) return;
        const cType = norm(driver.carType);
        if (!cType) return;
        if (!byCategory.has(cType)) {
            byCategory.set(cType, []);
        }
        byCategory.get(cType).push(driver);
    });

    const selectedTargets = [];
    let targetIndex = 0;

    byCategory.forEach((catDrivers) => {
        const sorted = [...catDrivers].sort((a, b) => (Number(a.distance || 0) - Number(b.distance || 0)));
        const topN = sorted.slice(0, maxPerCategory);

        topN.forEach(driver => {
            selectedTargets.push({
                targetIndex: targetIndex++,
                driverUid: driver.id || driver.uid,
                vehicleId: driver.activeCarId || (driver.car && driver.car.id) || null,
                carType: driver.carType,
                lat: Number(driver.location.lat),
                lng: Number(driver.location.lng),
                distance: Number(driver.distance || 0)
            });
        });
    });

    return selectedTargets;
};

/**
 * Réconcilie les résultats d'ETA Google Routes avec les chauffeurs par IDENTITÉ STRICTE (driverUid + vehicleId).
 * Vérifie l'état actuel des chauffeurs pour rejeter toute réponse devenue obsolète après un changement de véhicule.
 */
export const reconcileMatrixTargetsWithDrivers = (matrixTargets, etaResults, getCurrentDrivers) => {
    if (!Array.isArray(matrixTargets) || !Array.isArray(etaResults)) {
        return;
    }
    const liveDrivers = typeof getCurrentDrivers === 'function' ? getCurrentDrivers() : getCurrentDrivers;
    if (!Array.isArray(liveDrivers)) return;

    matrixTargets.forEach(target => {
        if (!target || !target.driverUid) return;

        // 1. Vérification que le chauffeur existe toujours dans l'état ACTUEL
        const liveDriver = liveDrivers.find(d => 
            (d.id && String(d.id) === String(target.driverUid)) || 
            (d.uid && String(d.uid) === String(target.driverUid))
        );
        if (!liveDriver) return;

        // 2. Vérification que le véhicule actif n'a pas changé pendant la requête Google
        const currentActiveCarId = liveDriver.activeCarId || (liveDriver.car && liveDriver.car.id);
        if (target.vehicleId && currentActiveCarId && String(currentActiveCarId) !== String(target.vehicleId)) {
            // Véhicule changé en cours de calcul : réponse Google rejetée !
            return;
        }

        // 3. Attribution de l'ETA ou secours géodésique immédiat
        const eta = etaResults[target.targetIndex];
        if (eta && eta.found) {
            liveDriver.arriveTime = eta;
        } else {
            const dist = typeof liveDriver.distance === 'number' ? liveDriver.distance : 0;
            liveDriver.arriveTime = {
                timein_text: `${Math.round((dist * 2) + 1)} min`,
                found: true,
                fallback: true
            };
        }
    });
};

/**
 * Détermine si une réponse UI asynchrone est obsolète en comparant son requestId au requestId courant.
 */
export const isUiResponseStale = (requestId, latestRequestId) => {
    return requestId !== latestRequestId;
};

/**
 * Construit la charge utile atomique multi-chemins pour la mise en service d'un véhicule :
 * - Vérifie la propriété du véhicule (doit appartenir au chauffeur).
 * - Bascule le flag active sur les véhicules de ce chauffeur uniquement.
 * - Met à jour le profil chauffeur (/users/$uid).
 * - Ne touche à la position GPS que si realGps dispose d'un horodatage réel et récent (< 5 min), sans inventer de timestamp.
 */
export const buildActiveVehicleAtomicPayload = (driverUid, selectedCar, allCars = [], realGps = null) => {
    if (!driverUid || !selectedCar || !selectedCar.id) {
        throw new Error("Paramètres invalides pour la mise en service du véhicule.");
    }
    const uid = String(driverUid);
    const isOwner = (selectedCar.driverId && String(selectedCar.driverId) === uid) ||
                    (selectedCar.driver && String(selectedCar.driver) === uid);
    if (!isOwner) {
        throw new Error("Action non autorisée : ce véhicule n'appartient pas à ce chauffeur.");
    }

    const myCars = (Array.isArray(allCars) ? allCars : []).filter(c => 
        c && ((c.driverId && String(c.driverId) === uid) || (c.driver && String(c.driver) === uid))
    );

    const updates = {};
    myCars.forEach(c => {
        updates[`cars/${c.id}/active`] = (c.id === selectedCar.id);
    });
    updates[`cars/${selectedCar.id}/active`] = true;

    const approvedCars = myCars.filter(c => c.approved || c.carApproved || c.id === selectedCar.id);
    const computedCarTypes = [...new Set(approvedCars.map(c => (c.carType || '').trim()).filter(Boolean))];

    updates[`users/${uid}/activeCarId`] = selectedCar.id;
    updates[`users/${uid}/carType`] = (selectedCar.carType || '').trim();
    updates[`users/${uid}/carTypes`] = computedCarTypes;
    updates[`users/${uid}/vehicleNumber`] = selectedCar.vehicleNumber || "";
    updates[`users/${uid}/vehicleMake`] = selectedCar.vehicleMake || "";
    updates[`users/${uid}/vehicleModel`] = selectedCar.vehicleModel || "";
    updates[`users/${uid}/other_info`] = selectedCar.other_info || "";
    updates[`users/${uid}/car_image`] = selectedCar.car_image || "";
    updates[`users/${uid}/updatedAt`] = Date.now();

    if (realGps && realGps.lat != null && realGps.lng != null) {
        const gpsTimestamp = typeof realGps.timestamp === 'number' ? realGps.timestamp : null;
        const isFresh = gpsTimestamp && (Date.now() - gpsTimestamp < 300000);
        if (isFresh) {
            updates[`locations/${uid}`] = {
                lat: Number(realGps.lat),
                lng: Number(realGps.lng),
                timestamp: gpsTimestamp
            };
            updates[`users/${uid}/location`] = {
                lat: Number(realGps.lat),
                lng: Number(realGps.lng)
            };
        }
    }

    return { updates, computedCarTypes };
};

/**
 * Construit la charge utile atomique multi-chemins pour le passage hors service :
 * Met à jour driverActiveStatus: false et supprime atomiquement la clé dans /locations/$uid (null).
 */
export const buildDriverOfflineAtomicPayload = (driverUid) => {
    if (!driverUid) throw new Error("driverUid requis pour la mise hors service.");
    const uid = String(driverUid);
    const updates = {};
    updates[`users/${uid}/driverActiveStatus`] = false;
    updates[`locations/${uid}`] = null;
    return updates;
};

/**
 * Limitation intelligente des requêtes Google Routes Distance Matrix :
 * Évite les appels redondants lors des micro-mouvements GPS tout en déclenchant
 * immédiatement le calcul lors d'un changement de véhicule, de chauffeur ou de déplacement significatif.
 */
export const shouldThrottleMatrixRequest = (
    lastCallInfo,
    currentTargets,
    minIntervalMs = 8000,
    minDistanceMeters = 50
) => {
    if (!lastCallInfo || !lastCallInfo.timestamp || !Array.isArray(lastCallInfo.targets)) {
        return false;
    }

    const now = Date.now();
    const elapsed = now - lastCallInfo.timestamp;

    // Si l'intervalle strict minimal n'est pas écoulé (< 8s)
    if (elapsed < minIntervalMs) {
        return true;
    }

    // Vérifier si la liste des cibles a changé de taille
    if (!Array.isArray(currentTargets) || currentTargets.length !== lastCallInfo.targets.length) {
        return false;
    }

    for (let i = 0; i < currentTargets.length; i++) {
        const cur = currentTargets[i];
        const prev = lastCallInfo.targets.find(t => t.driverUid === cur.driverUid);
        if (!prev) return false;
        if (prev.vehicleId !== cur.vehicleId) return false; // Changement de véhicule = recalcul immédiat !

        // Calcul du déplacement en mètres
        const dLat = (cur.lat - prev.lat) * 111320;
        const dLng = (cur.lng - prev.lng) * (111320 * Math.cos((cur.lat * Math.PI) / 180));
        const movedMeters = Math.sqrt(dLat * dLat + dLng * dLng);

        if (movedMeters > minDistanceMeters) {
            return false; // Déplacement réel significatif (> 50m) = recalcul immédiat !
        }
    }

    // Si moins de 30 secondes et que les chauffeurs sont stationnaires (< 50m)
    if (elapsed < 30000) {
        return true;
    }

    return false;
};
